import { JugadorDto, JugadoresEquipoActivoResponse, RegistroFisicoDto, CrearRegistroFisicoDto } from '@/types/jugador';
import { BaseService } from './BaseService';
import { ApiResponse} from '@/types';

/**
 * Servicio para gestionar jugadores
 */
export class JugadorService extends BaseService {
  constructor() {
    super('/jugadores');
  }

  /**
   * Obtiene los jugadores del equipo activo del entrenador autenticado
   * @returns Promise con los jugadores del equipo activo y información de temporada
   */
  async getJugadoresEquipoActivo(): Promise<ApiResponse<JugadoresEquipoActivoResponse>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/equipo-activo`, {
      method: 'GET',
      headers: this.getHeaders()
    });
    
    return this.handleResponse<ApiResponse<JugadoresEquipoActivoResponse>>(response);
  }

  /**
   * Obtiene un jugador por su ID
   * @param id - ID del jugador
   * @returns Promise con los datos del jugador
   */
  async getJugadorById(id: number): Promise<ApiResponse<JugadorDto>> {
    return this.getById<JugadorDto>(id);
  }

  /**
   * Crea un nuevo jugador
   * @param jugadorData - Datos del jugador a crear
   * @returns Promise con el jugador creado
   */
  async createJugador(jugadorData: Partial<JugadorDto>): Promise<ApiResponse<JugadorDto>> {
    return this.create<JugadorDto>(jugadorData);
  }

  /**
   * Actualiza un jugador existente
   * @param id - ID del jugador
   * @param jugadorData - Datos actualizados del jugador
   * @returns Promise con el jugador actualizado
   */
  async updateJugador(id: number, jugadorData: Partial<JugadorDto>): Promise<ApiResponse<JugadorDto>> {
    return this.update<JugadorDto>(id, jugadorData);
  }

  /**
   * Elimina un jugador
   * @param id - ID del jugador a eliminar
   * @returns Promise con la respuesta de eliminación
   */
  async deleteJugador(id: number): Promise<ApiResponse<void>> {
    return this.delete(id);
  }

  /**
   * Obtiene el último registro físico de un jugador
   * @param jugadorId - ID del jugador
   * @returns Promise con el último registro físico
   */
  async getUltimoRegistroFisico(jugadorId: number): Promise<ApiResponse<RegistroFisicoDto>> {
    const response = await fetch(`${this.baseURL}/registros-fisicos/jugador/${jugadorId}/ultimo`, {
      method: 'GET',
      headers: this.getHeaders()
    });
    
    return this.handleResponse<ApiResponse<RegistroFisicoDto>>(response);
  }

  /**
   * Crea un nuevo registro físico para un jugador
   * @param jugadorId - ID del jugador
   * @param registroData - Datos del registro físico
   * @returns Promise con el registro físico creado
   */
  async crearRegistroFisico(jugadorId: number, registroData: Omit<CrearRegistroFisicoDto, 'jugadorId'>): Promise<ApiResponse<RegistroFisicoDto>> {
    const registroCompleto: CrearRegistroFisicoDto = {
      ...registroData,
      jugadorId
    };
    
    const response = await fetch(`${this.baseURL}/registros-fisicos/crear`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(registroCompleto)
    });
    
    return this.handleResponse<ApiResponse<RegistroFisicoDto>>(response);
  }
}

// Instancia singleton del servicio
export const jugadorService = new JugadorService();